import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Loader,
} from 'lucide-react';

import { incidentsApiService } from '../../services/incidentsApi';
import MarkdownEditor from '../MarkdownEditor';
import type {
  AdoptCardAsIncidentRequest,
  Incident,
  IncidentKanbanLink,
  IncidentStatus,
} from '../../types/incident';

const STATUS_OPTIONS: IncidentStatus[] = [
  'investigating',
  'active',
  'critical',
  'pending',
  'resolved',
  'closed',
];

const PRIORITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * Default sync flags applied to every new bridge link. The five flags are no longer
 * individually exposed in the adopt UI — bridging two records is the explicit signal
 * that they should be kept in lock-step. Users who need finer control can toggle
 * individual flags later from the Workspaces panel on the incident detail page.
 */
const ALL_SYNC_ON: Required<
  Pick<
    AdoptCardAsIncidentRequest,
    'sync_status' | 'sync_priority' | 'sync_assignees' | 'sync_comments' | 'propagate_watchers'
  >
> = {
  sync_status: true,
  sync_priority: true,
  sync_assignees: true,
  sync_comments: true,
  propagate_watchers: true,
};

export interface AdoptAsIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardId: string;
  defaultTitle?: string;
  defaultDescription?: string;
  defaultPriority?: number;
  /**
   * Called on success. `createdIncident` is `false` when the backend reused an existing
   * bridged incident (idempotent path).
   */
  onSuccess: (incident: Incident, link: IncidentKanbanLink, createdIncident: boolean) => void;
}

/**
 * Reverse direction of "Promote to workspace": creates an `Incident` from this kanban card
 * and bridges them. Idempotent — if any bridge link already exists for the card, the
 * existing incident is returned (with `created_incident: false`).
 */
export const AdoptAsIncidentModal = ({
  isOpen,
  onClose,
  cardId,
  defaultTitle,
  defaultDescription,
  defaultPriority,
  onSuccess,
}: AdoptAsIncidentModalProps) => {
  const navigate = useNavigate();

  const [title, setTitle] = useState<string>(defaultTitle ?? '');
  const [description, setDescription] = useState<string>(defaultDescription ?? '');
  const [status, setStatus] = useState<IncidentStatus>('investigating');
  const [priority, setPriority] = useState<number>(defaultPriority ?? 5);
  const [copyAssignees, setCopyAssignees] = useState(true);
  // Sync flags default ON for every new bridge — not exposed here. See ALL_SYNC_ON below.
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ incident: Incident; createdIncident: boolean } | null>(
    null,
  );

  // Reset state when the modal opens / closes (preserve form on rapid re-open).
  useEffect(() => {
    if (!isOpen) {
      setSubmitting(false);
      setError(null);
      setSuccess(null);
      return;
    }
    setTitle(defaultTitle ?? '');
    setDescription(defaultDescription ?? '');
    setPriority(defaultPriority ?? 5);
    setStatus('investigating');
    setCopyAssignees(true);
    setNotes('');
  }, [isOpen, defaultTitle, defaultDescription, defaultPriority]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    const body: AdoptCardAsIncidentRequest = {
      ...(title.trim() ? { title: title.trim() } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
      status,
      priority,
      copy_assignees: copyAssignees,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...ALL_SYNC_ON,
    };
    try {
      const resp = await incidentsApiService.adoptCardAsIncident(cardId, body);
      setSuccess({ incident: resp.incident, createdIncident: resp.created_incident });
      onSuccess(resp.incident, resp.link, resp.created_incident);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to adopt card as incident';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenIncident = () => {
    if (!success) return;
    navigate(`/incidents/${encodeURIComponent(success.incident.id)}`);
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-[9999]"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="modal-panel max-w-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2 className="modal-title flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Adopt card as incident
            </h2>
            <button type="button" onClick={onClose} className="modal-close" disabled={submitting} aria-label="Close" title="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {success ? (
            <div className="space-y-4">
              <div className="alert alert-success" role="status">
                <CheckCircle />
                <div className="text-sm">
                  {success.createdIncident
                    ? 'Incident created and bridged to this card.'
                    : 'An existing bridged incident was reused — no duplicate created.'}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={onClose} className="glass-button-secondary">
                  Close
                </button>
                <button type="button" onClick={handleOpenIncident} className="btn-primary">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open incident
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-glass-400 mb-4">
                Creates an incident from this card and bridges them. If a bridge link already
                exists for this card, the existing incident is reused.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-glass-400 mb-1.5">
                    Incident title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="glass-input w-full"
                    placeholder="Defaults to the card title"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-glass-400 mb-1.5">
                    Description (markdown)
                  </label>
                  <MarkdownEditor
                    value={description}
                    onChange={setDescription}
                    height={180}
                    placeholder="Defaults to the card description, or its title if no description is set."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-glass-400 mb-1.5">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as IncidentStatus)}
                      className="glass-input w-full"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-glass-400 mb-1.5">
                      Priority (1 = highest)
                    </label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(Number(e.target.value))}
                      className="glass-input w-full"
                    >
                      {PRIORITY_OPTIONS.map((p) => (
                        <option key={p} value={p}>
                          P{p}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="rounded-md border border-purple-500/20 bg-purple-500/10 px-3 py-2 text-xs text-purple-200">
                  Status, priority, assignees, comments, and watchers will all sync between this
                  card and the new incident. Toggle individual flags later from the incident&apos;s
                  Workspaces panel if you need finer control.
                </div>

                <div className="text-sm text-glass-200">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={copyAssignees}
                      onChange={(e) => setCopyAssignees(e.target.checked)}
                    />
                    Copy this card&apos;s assignees onto the new incident
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-medium text-glass-400 mb-1.5">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Optional context recorded on the bridge link."
                    className="glass-input w-full"
                  />
                </div>
              </div>

              {error && (
                <div className="alert alert-danger mt-4" role="alert">
                  <AlertCircle />
                  <span className="break-words">{error}</span>
                </div>
              )}

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={onClose}
                  className="glass-button-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Adopting…
                    </>
                  ) : (
                    'Adopt as incident'
                  )}
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};
